/*
 *  -----------------------------------------------------------------------
 *    <copyright file="theme.ts" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import { createTheme, responsiveFontSizes, Theme, ThemeOptions, TypographyStyle } from '@mui/material/styles';

export type KendoTheme = 'legacy' | 'default';

const OpraPrimaryBlue = '#003366';
const OpraSecondaryBlue = '#336699';
const OpraPrimaryHover = '#000c38';

const ButtonTextPrimary = '#fff';
const ButtonHoverMidnightBlue = '#000c3b';
const ButtonTextHoverPrimary = '#333';
const BackgroundGrayed = '#fafafa';

const BorderLighter = '#c3c3c8';

const FontColor = '#212121';
const BodyText = '#616161';
const ErrorColor = '#ae0015';

const MontserratFont = ['Montserrat', 'sans-serif'].join(',');

const TextBlack = '#333';

const adaptedThemeV5FromV4: ThemeOptions = {
    palette: {
        primary: { main: OpraPrimaryBlue },
        secondary: { main: OpraSecondaryBlue },
    },
    breakpoints: {
        keys: ['xs', 'sm', 'md', 'lg', 'xl'],
        values: {
            xs: 0,
            sm: 420,
            md: 768,
            lg: 1280,
            xl: 1920,
        },
    },
    typography: {
        body1: {
            fontFamily: MontserratFont,
            fontWeight: 400,
            fontSize: '13px',
            color: TextBlack,
            textAlign: 'left',
            letterSpacing: '0.01071em',
            lineHeight: 1.43,
        } as TypographyStyle,
        body2: {
            fontFamily: MontserratFont,
            fontSize: '13px',
            fontWeight: 400,
            color: OpraPrimaryBlue,
            letterSpacing: 'normal',
        } as TypographyStyle,
        h1: {
            fontFamily: MontserratFont,
            fontSize: '36px',
            color: OpraPrimaryBlue,
            fontWeight: 'bold',
            textAlign: 'center',
            marginBottom: '20px',
        } as TypographyStyle,
        button: {
            fontFamily: MontserratFont,
            fontSize: '16px',
            color: ButtonTextPrimary,
            textTransform: 'none',
            lineHeight: 'normal',
        } as TypographyStyle,
    },
    components: {
        MuiTypography: {
            styleOverrides: {
                root: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    fontFamily: MontserratFont,
                    width: '100%',
                },
                h6: {
                    fontFamily: MontserratFont,
                    fontSize: '14px !important',
                },
            },
        },
        MuiDialogContent: {
            styleOverrides: {
                root: {
                    overflowX: 'hidden',
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    fontFamily: MontserratFont,
                    boxSizing: 'border-box',
                    textAlign: 'center',
                    borderRadius: '0px',
                    height: '42px',
                    minWidth: '100px',
                    fontSize: '14px',
                    fontWeight: 400,
                    variants: [
                        {
                            props: { variant: 'contained', color: 'primary' },
                            style: {
                                backgroundColor: OpraPrimaryBlue,
                                '&:hover': { backgroundColor: ButtonHoverMidnightBlue, boxShadow: 'none !important' },
                            },
                        },
                        {
                            props: { variant: 'contained', color: 'secondary' },
                            style: {
                                backgroundColor: OpraPrimaryBlue,
                                fontWeight: 300,
                                '&hover': { backgroundColor: ButtonHoverMidnightBlue, boxShadow: 'none !important' },
                            },
                        },
                        {
                            props: { variant: 'outlined', color: 'primary' },
                            style: {
                                border: `1px solid ${OpraSecondaryBlue}`,
                                color: OpraSecondaryBlue,
                                '&:hover': {
                                    color: ButtonTextPrimary,
                                    backgroundColor: OpraSecondaryBlue,
                                },
                            },
                        },
                        {
                            props: { variant: 'text', color: 'secondary' },
                            style: {
                                color: ButtonTextPrimary,
                            },
                        },
                    ],
                },
                contained: {
                    boxShadow: 'none !important',
                    fontWeight: '500 !important',
                    '&:disabled': {
                        backgroundColor: OpraPrimaryBlue,
                        color: ButtonTextPrimary,
                        opacity: 0.6,
                    },
                },
                sizeLarge: {
                    height: '50px',
                    fontSize: '16px',
                },
                sizeSmall: {
                    height: '20px',
                    minWidth: '150px',
                    fontSize: '10px',
                },
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    fontFamily: MontserratFont,
                    minHeight: 42,
                    backgroundColor: 'transparent',
                },
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    '@media (min-width: 420px)': {
                        minHeight: 31,
                    },
                },
            },
        },
        MuiFormLabel: {
            styleOverrides: {
                root: {
                    fontFamily: MontserratFont,
                    fontSize: '13px',
                    color: FontColor,
                    fontWeight: 500,
                    '&:focused': {
                        color: OpraSecondaryBlue,
                    },
                    error: {
                        color: ErrorColor,
                    },
                },
                colorSecondary: {
                    '&:focused': {
                        color: OpraSecondaryBlue,
                    },
                },
            },
        },
        MuiFormHelperText: {
            styleOverrides: {
                root: {
                    fontFamily: MontserratFont,
                    error: {
                        color: ErrorColor,
                        fontSize: 13,
                        fontStyle: 'italic',
                    },
                },
                contained: {
                    marginLeft: 0,
                    color: BorderLighter,
                },
            },
        },
        MuiFormControlLabel: {
            styleOverrides: {
                label: {
                    fontFamily: MontserratFont,
                    fontSize: '12px',
                    color: OpraSecondaryBlue,
                },
            },
        },
        MuiCheckbox: {
            styleOverrides: {
                root: {
                    fontSize: '20px',
                    color: OpraSecondaryBlue,
                    fontFamily: MontserratFont,
                    '&:checked': {
                        fontSize: '20px',
                        color: OpraSecondaryBlue,
                    },
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    fontFamily: MontserratFont,
                    fontSize: '12px',
                    backgroundColor: OpraSecondaryBlue,
                    color: '#FFF',
                    marginLeft: '10px',
                    textTransform: 'capitalize',
                },
                deleteIcon: {
                    color: '#FFF',
                    '&:hover': {
                        color: '#DDD',
                    },
                },
            },
        },
        MuiInput: {
            styleOverrides: {
                input: {
                    fontSize: 13,
                    fontFamily: MontserratFont,
                },
                underline: {
                    '&:hover&:not(.Mui-disabled)&:before': {
                        borderBottom: `1px solid ${OpraPrimaryBlue}`,
                    },
                },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                root: {
                    borderRadius: 0,
                    fontFamily: MontserratFont,
                    color: BodyText,
                    minHeight: 42,
                    '&:disabled,&.Mui-disabled': {
                        color: BodyText,
                        backgroundColor: BackgroundGrayed,
                    },
                },
                multiline: {
                    padding: '0 !important',
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                input: {
                    padding: 14,
                    fontFamily: MontserratFont,
                },
                adornedEnd: {
                    paddingRight: 0,
                    color: '#616161',
                },
                root: {
                    borderRadius: 0,
                    fontFamily: MontserratFont,
                    color: BodyText,
                    '&:hover:not($disabled):not($focused):not($error) $notchedOutline': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                    },
                    error: {
                        borderColor: ErrorColor,
                    },
                    '&:disabled': {
                        color: BodyText,
                    },
                },
            },
        },
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    fontFamily: MontserratFont,
                },
                shrink: {
                    transform: 'translate(14px, -6px) scale(1)',
                },
                outlined: {
                    transform: 'translate(14px, 14px) scale(1)',
                },
            },
        },
        MuiTabs: {
            styleOverrides: {
                root: {
                    fontFamily: MontserratFont,
                    borderBottom: `1px solid ${BorderLighter}`,
                    marginBottom: 5,
                    minHeight: 40,
                },
                list: {
                    display: 'block',
                },
            },
        },
        MuiSnackbar: {
            styleOverrides: {
                anchorOriginBottomRight: {
                    marginRight: 20,
                },
            },
        },
        MuiSnackbarContent: {
            styleOverrides: {
                root: {
                    borderRadius: 0,
                    maxWidth: 350,
                },
                action: {
                    position: 'absolute',
                    right: 20,
                    top: 10,
                },
                message: {
                    paddingRight: 30,
                    fontSize: 14,
                },
            },
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    fontSize: '12px',
                    minHeight: 40,
                },
                textColorPrimary: {
                    '&:selected': {
                        color: OpraPrimaryBlue,
                    },
                },
                textColorInherit: {
                    '&:selected': {
                        color: OpraPrimaryBlue,
                        fontWeight: 500,
                    },
                    color: BodyText,
                    fontWeight: 400,
                },
            },
        },
        MuiAvatar: {
            styleOverrides: {
                root: {
                    backgroundColor: OpraPrimaryBlue,
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                rounded: {
                    borderRadius: 0,
                },
            },
        },
        MuiDialogTitle: {
            styleOverrides: {
                root: {
                    backgroundColor: OpraPrimaryBlue,
                    color: '#fff',
                    paddingTop: 10,
                    paddingBottom: 10,
                    paddingLeft: 15,
                    paddingRight: 20,
                    fontWeight: 500,

                    '&+ .MuiDialogContent-root': {
                        paddingTop: '20px !important',
                    },
                },
            },
        },
        MuiRadio: {
            styleOverrides: {
                root: {
                    paddingBottom: 2,
                    paddingTop: 5,
                    color: OpraSecondaryBlue,
                },
                colorSecondary: {
                    color: OpraSecondaryBlue,
                    '&:checked': {
                        color: OpraSecondaryBlue,
                    },
                },
            },
        },
        MuiListItemButton: {
            styleOverrides: {
                root: {
                    width: '100%',
                },
            },
        },
        MuiListItemText: {
            styleOverrides: {
                primary: {
                    justifyContent: 'flex-start',
                    alignItems: 'flex-start',
                    color: BodyText,
                },
            },
        },
        MuiListSubheader: {
            styleOverrides: {
                root: {
                    fontFamily: MontserratFont,
                },
            },
        },
        MuiAccordion: {
            styleOverrides: {
                root: {
                    '&.Mui-expanded': {
                        margin: '0 !important',
                        '&:before': {
                            opacity: 1,
                        },
                    },
                },
            },
        },
        MuiAccordionSummary: {
            styleOverrides: {
                root: {
                    fontFamily: MontserratFont,
                },
            },
        },
        MuiBackdrop: {
            styleOverrides: {
                root: {
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                },
            },
        },
    },
};

const adaptedThemeWithV5Elements: ThemeOptions = {
    ...adaptedThemeV5FromV4,
    components: {
        ...adaptedThemeV5FromV4.components,
        MuiDivider: {
            ...adaptedThemeV5FromV4.components?.MuiDivider,
            styleOverrides: {
                ...adaptedThemeV5FromV4.components?.MuiDivider?.styleOverrides,
                root: ({ ownerState, theme }) => ({
                    ...(ownerState.orientation === 'vertical' &&
                        ownerState.variant === 'middle' && {
                            marginLeft: theme.spacing(2),
                            marginRight: theme.spacing(2),
                        }),
                }),
            },
        },
    },
};

const theme = createTheme(adaptedThemeWithV5Elements);

const OpraTheme = responsiveFontSizes(theme);

export default OpraTheme;
